// GenericCache: A reusable cache for any data type, with in-memory + localStorage, TTL, and clear/set/get methods.

export interface GenericCacheOptions {
  key: string;
  duration: number; // ms
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

export class GenericCache<T> {
  private memoryCache: CacheEntry<T> | null = null;
  private readonly key: string;
  private readonly duration: number;

  constructor(options: GenericCacheOptions) {
    this.key = options.key;
    this.duration = options.duration;
  }

  get(): T | null {
    if (this.memoryCache && Date.now() - this.memoryCache.timestamp < this.duration) {
      return this.memoryCache.data;
    }
    try {
      const stored = localStorage.getItem(this.key);
      if (stored) {
        const entry: CacheEntry<T> = JSON.parse(stored);
        if (Date.now() - entry.timestamp < this.duration) {
          this.memoryCache = entry;
          return entry.data;
        } else {
          localStorage.removeItem(this.key);
        }
      }
    } catch {}
    return null;
  }

  set(data: T): void {
    const entry: CacheEntry<T> = { data, timestamp: Date.now() };
    this.memoryCache = entry;
    try {
      localStorage.setItem(this.key, JSON.stringify(entry));
    } catch {}
  }

  clear(): void {
    this.memoryCache = null;
    try {
      localStorage.removeItem(this.key);
    } catch {}
  }
}
